import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked, cached } from '@glimmer/tracking';
import { service } from '@ember/service';
import FundModel from 'donor-elf-web/models/fund';
import JournalSummaryModel from 'donor-elf-web/models/journal-summary';
import Store from '@ember-data/store';
import { queryWithPages } from 'donor-elf-web/lib/store-utils';
import FundsService, {
  AllFundsInfo,
} from 'donor-elf-web/services/funds-service';
import { sortByProperties } from 'donor-elf-web/lib/array-utils';
import { htmlSafe } from '@ember/template';
import RouterService from '@ember/routing/router-service';

class SummaryItem {
  constructor(summary: JournalSummaryModel, fund?: FundModel) {
    this.summary = summary;
    this.fund = fund;
  }

  @tracked selected: boolean = false;
  fund?: FundModel;
  summary: JournalSummaryModel;
}

export default class JournalsSummariesIndexController extends Controller {
  @service declare fundsService: FundsService;
  @service declare store: Store;
  @service declare router: RouterService;

  @tracked loading: boolean = false;
  @tracked journalSummaries: JournalSummaryModel[] = [];
  @tracked items: SummaryItem[] = [];
  allFundsInfo?: AllFundsInfo;

  @tracked page: number = 1;
  perPage = 50;
  @tracked percentDownloaded: number = 0;

  get progressBarStyle() {
    return htmlSafe(`width: ${this.percentDownloaded}%`);
  }

  createItems() {
    let allFundsInfo = this.allFundsInfo!;

    this.items = sortByProperties<SummaryItem>(
      this.journalSummaries.map((summary) => {
        return new SummaryItem(summary, allFundsInfo.fundsById[summary.fundId]);
      }),
      ['fund.name', 'summary.startDate'],
    );
  }

  get selectedItems() {
    return this.items.filter((x) => x.selected);
  }

  get pagedItems(): SummaryItem[] {
    let offset = (this.page - 1) * this.perPage;
    return this.items.slice(offset, offset + this.perPage);
  }

  get totalPages() {
    return Math.ceil(this.items.length / this.perPage);
  }

  @action
  async templateDidInsert() {
    this.percentDownloaded = 5;
    this.loading = true;

    this.allFundsInfo = await this.fundsService.getAllFundsInfo();

    this.journalSummaries = await queryWithPages<JournalSummaryModel>({
      store: this.store,
      modelName: 'journal-summary',
      pageSize: 50,
      pageCallback: (args) => {
        let percent = Math.round(
          (args.downloadedCount * 100) / args.totalCount,
        );
        if (percent < 5) {
          percent = 5;
        }
        if (percent > 100) {
          percent = 100;
        }
        this.percentDownloaded = percent;
      },
    });

    this.createItems();

    this.loading = false;
  }

  @action
  viewSelectedJournals() {
    if (this.selectedItems.length > 0) {
      let ids = this.selectedItems.map((x) => x.summary.id).join('-');
      this.router.transitionTo('funds.fund.journals.summaries.view', {
        queryParams: { ids: ids },
      });
    }
  }

  @action
  toggleSelected(item: SummaryItem) {
    item.selected = !item.selected;
  }

  @action
  changePage(page: number) {
    this.page = page;
  }
}
